import { useCallback, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { redeemBetaCode } from "../../services/beta";
import { v } from "../../utils/runtime";

const BETA_QUERY_KEY = ["beta-status"];

/**
 * Экран закрытого бета-теста. Показывается ПЕРЕД онбордингом, если
 * `GET /beta/status` вернул `{enabled:true, redeemed:false}`. Юзеру нужно
 * ввести валидный инвайт-код и активировать его через `POST /beta/redeem`.
 *
 * После успешного redeem'а React-query инвалидирует запрос `beta-status` и
 * `RootLayout` пропускает юзера дальше в обычный флоу.
 *
 * Дизайн умышленно минималистичный — одна цель, одно действие. Логотип
 * Alfin наверху, крупный centered-input в моно-шрифте, единая CTA-кнопка.
 * Приводится к обеим темам через `theme.palette.mode`.
 */
export function BetaGate() {
  const theme = useTheme();
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("beta");
  const [code, setCode] = useState("");

  const mutation = useMutation({
    mutationFn: (raw: string) => redeemBetaCode(raw),
    onSuccess: () => {
      enqueueSnackbar(t("success"), { variant: "success" });
      // Инвалидируем и статус, и /me (пусть RootLayout пересчитает
      // проходит ли юзер дальше в онбординг).
      qc.invalidateQueries(BETA_QUERY_KEY);
      qc.invalidateQueries(["user-info"]);
    },
    onError: (err: unknown) => {
      const anyErr = err as {
        response?: { data?: { message?: string | string[] } };
        message?: string;
      };
      const raw =
        anyErr?.response?.data?.message ??
        anyErr?.message ??
        t("errors.generic");
      const msg = Array.isArray(raw) ? raw.join(", ") : raw;
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const handleSubmit = useCallback(() => {
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      enqueueSnackbar(t("errors.too_short"), { variant: "warning" });
      return;
    }
    mutation.mutate(trimmed);
  }, [code, enqueueSnackbar, mutation, t]);

  const logoSrc =
    theme.palette.mode === "dark" ? v("/alfin-logo.svg") : v("/alfin-logo-l.svg");

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        marginX: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "8px",
      }}
    >
      {/* -------- Лого + badge «closed beta» -------- */}
      <Box
        component="img"
        src={logoSrc}
        alt="Alfin"
        sx={{
          width: 80,
          height: "auto",
          display: "block",
          marginTop: "40px",
          marginBottom: "8px",
        }}
      />

      <Box
        sx={(t) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "999px",
          bgcolor:
            t.palette.mode === "dark"
              ? "rgba(245,166,35,0.14)"
              : "rgba(245,166,35,0.12)",
          color: "#F5A623",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          marginBottom: "20px",
        })}
      >
        <Icon icon="solar:key-square-2-bold-duotone" width={14} height={14} />
        {t("badge")}
      </Box>

      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.2,
          maxWidth: 320,
        }}
      >
        {t("title")}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          textAlign: "center",
          opacity: 0.65,
          marginTop: "8px",
          maxWidth: 320,
          lineHeight: 1.4,
        }}
      >
        {t("subtitle")}
      </Typography>

      {/* -------- Инпут кода -------- */}
      <Box
        component="input"
        value={code}
        onChange={(e) =>
          setCode(
            (e.target as HTMLInputElement).value
              .toUpperCase()
              .replace(/[^A-Z0-9-]/g, "")
              .slice(0, 32),
          )
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
        placeholder={t("placeholder")}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        sx={(t) => ({
          marginTop: "28px",
          width: "100%",
          padding: "16px 18px",
          fontSize: 20,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontWeight: 700,
          letterSpacing: 4,
          textAlign: "center",
          textTransform: "uppercase",
          border: "1px solid",
          borderColor:
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.10)"
              : "rgba(0,0,0,0.10)",
          borderRadius: "14px",
          background:
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)",
          color: t.palette.text.primary,
          outline: "none",
          transition: "border-color 0.15s, background 0.15s",
          "&:focus": {
            borderColor: t.palette.primary.main,
            background:
              t.palette.mode === "dark"
                ? "rgba(51,119,255,0.08)"
                : "rgba(51,119,255,0.06)",
          },
          "&::placeholder": {
            color:
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.28)"
                : "rgba(0,0,0,0.28)",
            letterSpacing: 2,
            fontSize: 16,
            textTransform: "none",
          },
        })}
      />

      {/* -------- CTA -------- */}
      <ButtonBase
        onClick={handleSubmit}
        disabled={mutation.isLoading || code.trim().length < 4}
        sx={{
          marginTop: "18px",
          width: "100%",
          minHeight: 52,
          borderRadius: "16px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: 16,
          fontWeight: 600,
          transition: "opacity 0.15s, background 0.15s",
          "&:active": { opacity: 0.9 },
          "&.Mui-disabled": {
            opacity: 0.4,
            color: "primary.contrastText",
          },
        }}
      >
        {mutation.isLoading ? (
          <CircularProgress size={20} sx={{ color: "primary.contrastText" }} />
        ) : (
          t("cta")
        )}
      </ButtonBase>

      <Typography
        sx={{
          marginTop: "18px",
          fontSize: 12,
          opacity: 0.5,
          textAlign: "center",
          lineHeight: 1.5,
          maxWidth: 300,
        }}
      >
        {t("hint")}
      </Typography>
    </Box>
  );
}
